import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";

// ── BRAND SYSTEM ─────────────────────────────────────────────
const EP = {
  lavender:  "#8B6BAE",
  lavLight:  "#A888CC",
  lavDark:   "#6B4B8E",
  lavSoft:   "#F7F3FC",
  lavPale:   "#F0EAFA",
  lavBorder: "#EDE8F5",
  lavMid:    "#D9D0EC",
  gold:      "#C4A45A",
  cream:     "#FDFAF6",
  dark:      "#2D2040",
  mid:       "#6B5080",
  light:     "#A898C0",
  white:     "#FFFFFF",
  footerBg:  "#2D2040",
};

// ── CATEGORY DATA ────────────────────────────────────────────
const CATEGORIES = [
  { slug: "home-garden-tools",   label: "Home, Garden & Tools",  icon: "home",    color: "#7B9E8F", desc: "Everything for your home, yard, and workshop." },
  { slug: "pets",                label: "Pets",                   icon: "paw",     color: "#C47FAA", desc: "Products for your furry, feathered, and scaly family members." },
  { slug: "devices-electronics", label: "Devices & Electronics",  icon: "devices", color: "#7B8FAE", desc: "Tech picks, gadgets, and smart home essentials." },
  { slug: "books-reading",       label: "Books & Reading",        icon: "book",    color: "#C4A45A", desc: "Books, e-readers, and everything for book lovers." },
  { slug: "automotive",          label: "Automotive",             icon: "car",     color: "#8B7BAE", desc: "Car care, accessories, and road trip gear." },
  { slug: "sports-outdoors",     label: "Sports & Outdoors",      icon: "sports",  color: "#7B9E8F", desc: "Gear for every adventure and workout." },
  { slug: "fashion-beauty",      label: "Fashion & Beauty",       icon: "fashion", color: "#C47FAA", desc: "Style picks, skincare, and beauty favorites." },
  { slug: "groceries",           label: "Groceries",              icon: "grocery", color: "#8BAA6B", desc: "Pantry staples and kitchen must-haves." },
  { slug: "music-video-gaming",  label: "Music, Video & Gaming",  icon: "gaming",  color: "#8B6BAE", desc: "Entertainment for gamers and music lovers." },
  { slug: "toys-kids-baby",      label: "Toys, Kids & Baby",      icon: "toy",     color: "#C4845A", desc: "Safe, fun picks for little ones." },
  { slug: "office-professional", label: "Office & Professional",  icon: "office",  color: "#7B8FAE", desc: "Everything for your workspace and career." },
  { slug: "miscellaneous",       label: "Miscellaneous",          icon: "misc",    color: "#C4A45A", desc: "Unique finds that don't fit anywhere else." },
];

// ── SVG ICON SET ─────────────────────────────────────────────
const ICONS = {
  home:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9.5z"/><path d="M9 21V12h6v9"/></svg>`,
  paw:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="4" r="2"/><circle cx="18" cy="8" r="2"/><circle cx="4" cy="8" r="2"/><circle cx="7" cy="14" r="2"/><path d="M16 14c1 2.5 0 5-2.5 6.5S7 21 7 18.5c0-1.5 1-3 2-4l2-2 2 1 2.5 1.5z"/></svg>`,
  devices: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12" y2="18.01"/></svg>`,
  book:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>`,
  car:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 17H3v-5l2.5-6h11L19 12v5h-2"/><circle cx="7.5" cy="17.5" r="1.5"/><circle cx="16.5" cy="17.5" r="1.5"/><path d="M5 12h14"/></svg>`,
  sports:  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/><line x1="2" y1="12" x2="22" y2="12"/></svg>`,
  fashion: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>`,
  grocery: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>`,
  gaming:  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/><line x1="15" y1="13" x2="15" y2="13.01"/><line x1="18" y1="11" x2="18" y2="11.01"/><rect x="2" y="8" width="20" height="12" rx="6"/></svg>`,
  toy:     `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M8 8H4a1 1 0 0 0-1 1v9a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a1 1 0 0 0-1-1h-4"/></svg>`,
  office:  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2"/><line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/></svg>`,
  misc:    `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`,
};

// ── BLOG POSTS ───────────────────────────────────────────────
const POSTS = [
  {
    slug: "top-5-smart-home-gadgets",
    title: "Top 5 Smart Home Gadgets I Can't Live Without",
    date: "March 28, 2026",
    category: "Devices & Electronics",
    categorySlug: "devices-electronics",
    excerpt: "If you've been thinking about making your home a little smarter — these are the five gadgets I recommend to literally everyone. Affordable, easy to set up, and genuinely life-changing.",
    body: `If you've been thinking about making your home a little smarter — these are the five gadgets I recommend to literally everyone. They're affordable, easy to set up, and genuinely make everyday life easier.\n\nI've been testing smart home products for over two years now, and these five have stayed on my must-have list the entire time. Whether you're just getting started or you're already a tech enthusiast, there's something here for every budget.\n\nYou'll find all my current picks in the Devices & Electronics section. Every link is an affiliate link so I earn a small commission at no extra cost to you. Thank you so much for your support! 💛`,
  },
  {
    slug: "best-garden-tools-spring",
    title: "The Best Garden Tools for Spring — Tested & Approved",
    date: "March 15, 2026",
    category: "Home, Garden & Tools",
    categorySlug: "home-garden-tools",
    excerpt: "Spring is finally here and I have been out in the garden every single weekend. After trying more tools than I can count, here are the ones I actually keep reaching for.",
    body: `Spring is finally here and I have been out in the garden every single weekend. After trying more tools than I can count, here are the ones I actually keep reaching for.\n\nGardening doesn't have to be expensive, but having the right tools makes such a difference. I've linked everything in the Home, Garden & Tools section so you can shop directly.\n\nHappy planting! 🌱`,
  },
  {
    slug: "pet-products-actually-worth-it",
    title: "Pet Products That Are Actually Worth the Money",
    date: "March 3, 2026",
    category: "Pets",
    categorySlug: "pets",
    excerpt: "My two dogs and one very opinionated cat have helped me test a LOT of pet products over the years. Here's what actually made the cut — and what I'd skip.",
    body: `My two dogs and one very opinionated cat have helped me test a LOT of pet products over the years. Here's what actually made the cut — and what I'd skip.\n\nYou'll find all of my current pet recommendations in the Pets section. Every link is an affiliate link. Thank you so much for supporting eProduct Promotion! 🐾`,
  },
];

// ── REUSABLE COMPONENTS ──────────────────────────────────────
function BtnPrimary({ children, onClick, style = {} }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? EP.lavLight : EP.lavender,
        color: EP.white, border: "none", borderRadius: 100,
        fontFamily: "'Nunito',sans-serif", fontWeight: 800,
        fontSize: 15, padding: "12px 30px", cursor: "pointer",
        transition: "all .2s",
        boxShadow: hov ? `0 8px 24px rgba(139,107,174,.35)` : `0 4px 12px rgba(139,107,174,.2)`,
        transform: hov ? "translateY(-2px)" : "none", ...style
      }}>{children}</button>
  );
}

function BtnOutline({ children, onClick, style = {} }) {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? EP.lavSoft : "transparent",
        color: EP.lavender, border: `2px solid ${EP.lavender}`,
        borderRadius: 100, fontFamily: "'Nunito',sans-serif",
        fontWeight: 800, fontSize: 15,
        padding: "10px 26px", cursor: "pointer",
        transition: "all .2s",
        transform: hov ? "translateY(-2px)" : "none", ...style
      }}>{children}</button>
  );
}

function SectionLabel({ children, center = false, style = {} }) {
  return (
    <div style={{
      fontFamily: "'Nunito',sans-serif", fontWeight: 800,
      fontSize: 10, letterSpacing: 4, color: EP.lavender,
      textTransform: "uppercase", lineHeight: 1,
      textAlign: center ? "center" : "left", ...style,
    }}>{children}</div>
  );
}

function SectionHeading({ children, center = false, light = false, style = {} }) {
  return (
    <h2 style={{
      fontFamily: "'Playfair Display',serif",
      fontSize: "clamp(26px,3.5vw,38px)",
      color: light ? EP.white : EP.dark,
      lineHeight: 1.2, margin: "0 0 14px",
      textAlign: center ? "center" : "left", ...style
    }}>{children}</h2>
  );
}

function Body({ children, center = false, light = false, style = {} }) {
  return (
    <p style={{
      fontFamily: "'Nunito',sans-serif", fontWeight: 400,
      fontSize: 16, lineHeight: 1.75,
      color: light ? "rgba(253,250,246,.82)" : EP.mid,
      margin: 0, textAlign: center ? "center" : "left", ...style
    }}>{children}</p>
  );
}

function Section({ children, bg = EP.cream, style = {}, id }) {
  return (
    <section id={id} style={{ background: bg, padding: "72px 32px", ...style }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>{children}</div>
    </section>
  );
}

// ── CATEGORY TILE ────────────────────────────────────────────
function CatTile({ cat, onClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div onClick={() => onClick(cat)}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? EP.lavSoft : EP.white,
        borderRadius: 12,
        border: `1.5px solid ${EP.lavBorder}`,
        cursor: "pointer",
        transition: "background .2s, box-shadow .2s, transform .2s",
        display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "space-between",
        padding: "20px 10px 16px",
        height: 128, width: "100%",
        boxSizing: "border-box",
        boxShadow: hov ? `0 8px 24px rgba(139,107,174,.15)` : "0 2px 10px rgba(0,0,0,.04)",
        transform: hov ? "translateY(-3px)" : "none",
      }}>
      <div style={{ width: 34, height: 34, color: cat.color, flexShrink: 0 }}
        dangerouslySetInnerHTML={{ __html: ICONS[cat.icon] }} />
      <div style={{
        fontFamily: "'Nunito',sans-serif", fontWeight: 700,
        fontSize: 11, color: EP.dark, lineHeight: 1.3,
        textAlign: "center", flex: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "4px 4px 0",
      }}>{cat.label}</div>
      <div style={{
        fontFamily: "'Nunito',sans-serif", fontWeight: 600,
        fontSize: 10, color: "#B0A8C0", letterSpacing: .3, flexShrink: 0,
      }}>Shop Now →</div>
    </div>
  );
}

// ── SOCIAL ICON ──────────────────────────────────────────────
const SOC_PATHS = {
  pinterest: "M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z",
  youtube:   "M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z",
  tiktok:    "M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.19 8.19 0 0 0 4.79 1.53V6.77a4.85 4.85 0 0 1-1.02-.08z",
  instagram: "M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z",
};

function SocIcon({ name, url, title, light = false }) {
  const [hov, setHov] = useState(false);
  const fill = light ? (hov ? EP.white : "rgba(255,255,255,.7)") : EP.lavender;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" title={title}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        width: 34, height: 34, borderRadius: "50%",
        background: light ? (hov ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.1)") : (hov ? EP.lavBorder : EP.lavSoft),
        border: `1.5px solid ${light ? "rgba(255,255,255,.25)" : (hov ? EP.lavender : EP.lavMid)}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        textDecoration: "none", transition: "all .2s", flexShrink: 0,
      }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill={fill}>
        <path d={SOC_PATHS[name]} />
      </svg>
    </a>
  );
}

// ── FOOTER ───────────────────────────────────────────────────
function Footer({ setPage }) {
  const socials = [
    { name: "pinterest", url: "https://pinterest.com",                  title: "Pinterest" },
    { name: "youtube",   url: "https://youtube.com",                     title: "YouTube" },
    { name: "tiktok",    url: "https://tiktok.com",                      title: "TikTok" },
    { name: "instagram", url: "https://instagram.com/eProductPromotion", title: "@eProductPromotion" },
  ];
  return (
    <footer style={{ background: EP.footerBg, padding: "52px 32px 28px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr 1fr", gap: 48, marginBottom: 40 }} className="footer-grid">
          <div>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, color: EP.white, marginBottom: 12 }}>
              eProduct <span style={{ color: EP.lavender }}>Promotion</span>
            </div>
            <Body light style={{ fontSize: 14, marginBottom: 18 }}>
              Your warm and friendly guide to the best products across every category — curated with love.
            </Body>
            <div style={{ display: "flex", gap: 9 }}>
              {socials.map(s => <SocIcon key={s.name} {...s} light />)}
            </div>
          </div>
          <div>
            <SectionLabel style={{ marginBottom: 14 }}>Categories</SectionLabel>
            {CATEGORIES.slice(0, 6).map(c => (
              <div key={c.slug} onClick={() => setPage(`category/${c.slug}`)}
                style={{ fontFamily: "'Nunito',sans-serif", fontSize: 13, color: "rgba(253,250,246,.45)", marginBottom: 8, cursor: "pointer", transition: "color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color = EP.lavender}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(253,250,246,.45)"}
              >{c.label}</div>
            ))}
          </div>
          <div>
            <SectionLabel style={{ marginBottom: 14 }}>More</SectionLabel>
            {CATEGORIES.slice(6).map(c => (
              <div key={c.slug} onClick={() => setPage(`category/${c.slug}`)}
                style={{ fontFamily: "'Nunito',sans-serif", fontSize: 13, color: "rgba(253,250,246,.45)", marginBottom: 8, cursor: "pointer", transition: "color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color = EP.lavender}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(253,250,246,.45)"}
              >{c.label}</div>
            ))}
            <SectionLabel style={{ marginBottom: 14, marginTop: 20 }}>Pages</SectionLabel>
            {["Blog", "Contact"].map(p => (
              <div key={p} onClick={() => setPage(p.toLowerCase())}
                style={{ fontFamily: "'Nunito',sans-serif", fontSize: 13, color: "rgba(253,250,246,.45)", marginBottom: 8, cursor: "pointer", transition: "color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color = EP.lavender}
                onMouseLeave={e => e.currentTarget.style.color = "rgba(253,250,246,.45)"}
              >{p}</div>
            ))}
          </div>
        </div>
        <div style={{ borderTop: "1px solid rgba(255,255,255,.07)", paddingTop: 20, display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 12, color: "rgba(253,250,246,.28)" }}>© 2026 eProduct Promotion · Melanie Byrd</div>
          <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 12, color: "rgba(253,250,246,.28)" }}>This site contains affiliate links. I earn a small commission at no extra cost to you. 💛</div>
        </div>
      </div>
    </footer>
  );
}

// ── MAIN INNER COMPONENT ─────────────────────────────────────
function EProductInner() {
  const navigate = useNavigate();
  const location = useLocation();

  const pathToPage = (path) => {
    if (path === "/" || path === "") return "home";
    if (path === "/blog") return "blog";
    if (path === "/contact") return "contact";
    if (path.startsWith("/blog/")) return `post/${path.replace("/blog/", "")}`;
    if (path.startsWith("/category/")) return path.replace("/", "");
    return "home";
  };
  const pageToPath = (p) => {
    if (p === "home") return "/";
    if (p === "blog") return "/blog";
    if (p === "contact") return "/contact";
    if (p.startsWith("post/")) return `/blog/${p.replace("post/", "")}`;
    if (p.startsWith("category/")) return `/${p}`;
    return "/";
  };

  const page = pathToPage(location.pathname);
  function setPage(p) { navigate(pageToPath(p)); }

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactForm, setContactForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [contactSent, setContactSent] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [location.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => {
    const BASE = "https://www.eproductpromotion.com";
    const cat = CATEGORIES.find(c => page === `category/${c.slug}`);
    const post = POSTS.find(p => page === `post/${p.slug}`);
    const SEO = {
      home:    { title: "eProduct Promotion | Curated Shortcuts to Products We Love", desc: "Melanie curates the best affiliate products across 12 categories — Home & Garden, Pets, Electronics, Fashion, and more." },
      blog:    { title: "Blog | eProduct Promotion", desc: "Read Melanie's latest product recommendations and honest reviews." },
      contact: { title: "Contact | eProduct Promotion", desc: "Get in touch with Melanie Byrd at eProduct Promotion." },
    };
    let meta = SEO[page] || SEO.home;
    if (cat) meta = { title: `${cat.label} | eProduct Promotion`, desc: `Browse Melanie's hand-picked ${cat.label} picks.` };
    if (post) meta = { title: `${post.title} | eProduct Promotion`, desc: post.excerpt };
    document.title = meta.title;
    const sm = (a, k, v) => { let e = document.querySelector(`meta[${a}="${k}"]`); if (!e) { e = document.createElement("meta"); e.setAttribute(a, k); document.head.appendChild(e); } e.setAttribute("content", v); };
    const sl = (r, h) => { let e = document.querySelector(`link[rel="${r}"]`); if (!e) { e = document.createElement("link"); e.setAttribute("rel", r); document.head.appendChild(e); } e.setAttribute("href", h); };
    sm("name", "description", meta.desc);
    sm("property", "og:title", meta.title);
    sm("property", "og:description", meta.desc);
    sl("canonical", `${BASE}${location.pathname}`);
  }, [page]);

  async function submitContact() {
    if (!contactForm.name || !contactForm.email) return;
    try {
      await fetch("https://formspree.io/f/PLACEHOLDER", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json" },
        body: JSON.stringify({ ...contactForm, _subject: `Contact — ${contactForm.name}`, _replyto: contactForm.email }),
      });
    } catch (e) {}
    setContactSent(true);
  }

  const isHome     = page === "home";
  const isBlog     = page === "blog";
  const isContact  = page === "contact";
  const isCategory = page.startsWith("category/");
  const isPost     = page.startsWith("post/");
  const currentCat  = isCategory ? CATEGORIES.find(c => page === `category/${c.slug}`) : null;
  const currentPost = isPost ? POSTS.find(p => page === `post/${p.slug}`) : null;

  const inputStyle = {
    width: "100%", background: EP.white,
    border: `1.5px solid ${EP.lavBorder}`,
    borderRadius: 10, padding: "13px 16px",
    fontSize: 15, color: EP.dark,
    fontFamily: "'Nunito',sans-serif",
    outline: "none", transition: "border-color .2s",
  };

  const postTag = (label) => (
    <span style={{
      background: EP.lavPale, color: EP.lavender,
      fontFamily: "'Nunito',sans-serif", fontWeight: 800,
      fontSize: 9, letterSpacing: 2, textTransform: "uppercase",
      padding: "2px 8px", borderRadius: 100,
    }}>{label}</span>
  );

  const divLine = { border: "none", borderBottom: `1.5px solid ${EP.lavBorder}`, margin: "10px 0 14px" };

  const socials = [
    { name: "pinterest", url: "https://pinterest.com",                  title: "Pinterest" },
    { name: "youtube",   url: "https://youtube.com",                     title: "YouTube" },
    { name: "tiktok",    url: "https://tiktok.com",                      title: "TikTok" },
    { name: "instagram", url: "https://instagram.com/eProductPromotion", title: "@eProductPromotion" },
  ];

  return (
    <div style={{ fontFamily: "'Nunito',sans-serif", background: EP.cream, minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Nunito:wght@400;600;700;800&family=Dancing+Script:wght@600&display=swap');
        *{box-sizing:border-box;}
        @keyframes fadeUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0}to{opacity:1}}
        .ep-input:focus{border-color:${EP.lavender}!important;}
        @media(max-width:900px){
          .main-grid{grid-template-columns:1fr!important;}
          .blog-sidebar{position:static!important;}
          .footer-grid{grid-template-columns:1fr!important;gap:28px!important;}
          .contact-grid{grid-template-columns:1fr!important;}
          .blog-grid{grid-template-columns:1fr 1fr!important;}
        }
        @media(max-width:640px){
          .desktop-nav{display:none!important;}
          #mob-btn{display:flex!important;}
          .tile-col-grid{grid-template-columns:repeat(2,1fr)!important;}
          .blog-grid{grid-template-columns:1fr!important;}
        }
        @media(max-width:380px){
          .tile-col-grid{grid-template-columns:1fr!important;}
        }
      `}</style>

      {/* ── NAV ─────────────────────────────────────────── */}
      <nav style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: scrolled ? "rgba(253,250,246,.97)" : "transparent",
        backdropFilter: scrolled ? "blur(12px)" : "none",
        borderBottom: scrolled ? `1.5px solid ${EP.lavBorder}` : "1.5px solid transparent",
        transition: "all .3s", padding: "0 28px",
      }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", height: 62, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div onClick={() => setPage("home")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 9 }}>
            <svg width="28" height="28" viewBox="0 0 30 30" fill="none">
              <circle cx="15" cy="15" r="15" fill={EP.lavender} />
              <path d="M15 6L16.5 13L23 15L16.5 17L15 24L13.5 17L7 15L13.5 13Z" fill="white" />
            </svg>
            <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 17, color: EP.dark, lineHeight: 1 }}>
              eProduct <span style={{ color: EP.lavender }}>Promotion</span>
            </div>
          </div>
          <div className="desktop-nav" style={{ display: "flex", alignItems: "center", gap: 28 }}>
            {[{ label: "Shop by Category", p: "home" }, { label: "Blog", p: "blog" }, { label: "Contact", p: "contact" }].map(n => (
              <div key={n.p} onClick={() => setPage(n.p)}
                style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 14, color: EP.dark, cursor: "pointer", transition: "color .2s" }}
                onMouseEnter={e => e.currentTarget.style.color = EP.lavender}
                onMouseLeave={e => e.currentTarget.style.color = EP.dark}
              >{n.label}</div>
            ))}
          </div>
          <button id="mob-btn" onClick={() => setMenuOpen(!menuOpen)}
            style={{ display: "none", flexDirection: "column", gap: 5, background: "none", border: "none", cursor: "pointer", padding: 8 }}>
            {[0, 1, 2].map(i => <div key={i} style={{ width: 22, height: 2, background: EP.dark, borderRadius: 2 }} />)}
          </button>
        </div>
        {menuOpen && (
          <div style={{ background: EP.white, borderTop: `1.5px solid ${EP.lavBorder}`, padding: "16px 28px 20px" }}>
            {[{ label: "Shop by Category", p: "home" }, { label: "Blog", p: "blog" }, { label: "Contact", p: "contact" }].map(n => (
              <div key={n.p} onClick={() => { setPage(n.p); setMenuOpen(false); }}
                style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 16, color: EP.dark, padding: "11px 0", borderBottom: `1px solid ${EP.lavBorder}`, cursor: "pointer" }}
              >{n.label}</div>
            ))}
          </div>
        )}
      </nav>

      {/* ════════════════════════════════════════════════════
          HOME PAGE
      ════════════════════════════════════════════════════ */}
      {isHome && (
        <div style={{ animation: "fadeIn .5s ease", paddingTop: 62 }}>
          {/* Hero */}
          <div style={{ background: "linear-gradient(135deg,#7B5A9E 0%,#9B7CBE 55%,#B89FD0 100%)", padding: "36px 28px 30px", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: -40, right: -40, width: 180, height: 180, borderRadius: "50%", background: "rgba(255,255,255,.07)", pointerEvents: "none" }} />
            <div style={{ position: "absolute", bottom: -20, left: "10%", width: 110, height: 110, borderRadius: "50%", background: "rgba(242,186,59,.09)", pointerEvents: "none" }} />
            <div style={{ maxWidth: 1100, margin: "0 auto", textAlign: "center", position: "relative", zIndex: 1 }}>
              <div style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 9, letterSpacing: 5, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 10 }}>
                Welcome to eProduct Promotion
              </div>
              <h1 style={{ fontFamily: "'Dancing Script',cursive", fontSize: "clamp(30px,4vw,44px)", color: EP.white, lineHeight: 1.25, margin: 0, fontWeight: 600 }}>
                Curated Shortcuts to Products We Love.
              </h1>
            </div>
          </div>

          {/* Main 3-column grid */}
          <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 28px 48px", display: "grid", gridTemplateColumns: "1fr 1fr 280px", gap: 28, alignItems: "start" }} className="main-grid">

            {/* Tile col 1 */}
            <div>
              <SectionLabel>Shop by Category</SectionLabel>
              <hr style={divLine} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="tile-col-grid">
                {CATEGORIES.slice(0, 6).map(cat => <CatTile key={cat.slug} cat={cat} onClick={() => setPage(`category/${cat.slug}`)} />)}
              </div>
            </div>

            {/* Tile col 2 — invisible label preserves alignment */}
            <div>
              <SectionLabel style={{ color: "transparent", userSelect: "none" }}>.</SectionLabel>
              <hr style={divLine} />
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }} className="tile-col-grid">
                {CATEGORIES.slice(6).map(cat => <CatTile key={cat.slug} cat={cat} onClick={() => setPage(`category/${cat.slug}`)} />)}
              </div>
            </div>

            {/* Blog sidebar */}
            <div className="blog-sidebar" style={{ position: "sticky", top: 74 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <SectionLabel>Latest Posts</SectionLabel>
                <button onClick={() => setPage("blog")} style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 10, letterSpacing: 3, color: EP.lavender, textTransform: "uppercase", background: "none", border: "none", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3, padding: 0 }}>
                  View All
                </button>
              </div>
              <hr style={divLine} />

              {POSTS.map(post => (
                <div key={post.slug} onClick={() => setPage(`post/${post.slug}`)}
                  style={{ background: EP.white, borderRadius: 10, border: `1.5px solid ${EP.lavBorder}`, cursor: "pointer", marginBottom: 10, padding: "12px 14px", transition: "transform .2s, box-shadow .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = `0 4px 14px rgba(139,107,174,.1)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  {postTag(post.category)}
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 13, color: EP.dark, lineHeight: 1.35, margin: "6px 0 3px" }}>{post.title}</div>
                  <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 11, color: EP.light }}>{post.date}</div>
                </div>
              ))}

              {/* Follow Us */}
              <div style={{ background: EP.lavSoft, borderRadius: 12, padding: "14px 16px", border: `1.5px solid ${EP.lavBorder}`, marginTop: 4 }}>
                <SectionLabel style={{ marginBottom: 12 }}>Follow Us</SectionLabel>
                <div style={{ display: "flex", gap: 8 }}>
                  {socials.map(s => <SocIcon key={s.name} {...s} />)}
                </div>
              </div>
            </div>
          </div>

          <Footer setPage={setPage} />
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          CATEGORY PAGE
      ════════════════════════════════════════════════════ */}
      {isCategory && currentCat && (
        <div style={{ animation: "fadeIn .5s ease", paddingTop: 62 }}>
          <div style={{ background: `linear-gradient(135deg,${currentCat.color}18,${currentCat.color}30)`, padding: "56px 28px 44px", textAlign: "center" }}>
            <div style={{ width: 52, height: 52, color: currentCat.color, margin: "0 auto 16px" }} dangerouslySetInnerHTML={{ __html: ICONS[currentCat.icon] }} />
            <SectionLabel center style={{ marginBottom: 10 }}>{currentCat.label}</SectionLabel>
            <SectionHeading center>{currentCat.label} Picks</SectionHeading>
            <Body center style={{ maxWidth: 480, margin: "0 auto 16px" }}>{currentCat.desc}</Body>
            <button onClick={() => setPage("home")} style={{ background: "none", border: "none", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 14, color: EP.lavender, cursor: "pointer" }}>
              ← Back to All Categories
            </button>
          </div>
          <Section>
            <div style={{ background: EP.white, borderRadius: 14, padding: 48, border: `2px dashed ${EP.lavBorder}`, textAlign: "center", marginBottom: 36 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, color: EP.dark, marginBottom: 10 }}>Products Coming Soon!</div>
              <Body center style={{ maxWidth: 440, margin: "0 auto 22px" }}>Melanie is curating her favorite {currentCat.label.toLowerCase()} picks right now. Check back soon!</Body>
              <BtnPrimary onClick={() => setPage("home")}>Browse Other Categories</BtnPrimary>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 16 }} className="blog-grid">
              {Array.from({ length: 6 }, (_, i) => (
                <div key={i} style={{ background: EP.white, borderRadius: 12, border: `1.5px solid ${EP.lavBorder}`, overflow: "hidden", opacity: .4 }}>
                  <div style={{ background: EP.lavSoft, height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 40, height: 40, color: currentCat.color }} dangerouslySetInnerHTML={{ __html: ICONS[currentCat.icon] }} />
                  </div>
                  <div style={{ padding: 14 }}>
                    <div style={{ background: EP.lavBorder, height: 12, borderRadius: 4, marginBottom: 8, width: "80%" }} />
                    <div style={{ background: EP.lavBorder, height: 10, borderRadius: 4, width: "60%" }} />
                    <div style={{ background: EP.lavPale, height: 32, borderRadius: 100, marginTop: 14 }} />
                  </div>
                </div>
              ))}
            </div>
          </Section>
          <Footer setPage={setPage} />
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          BLOG PAGE
      ════════════════════════════════════════════════════ */}
      {isBlog && (
        <div style={{ animation: "fadeIn .5s ease", paddingTop: 62 }}>
          <div style={{ background: `linear-gradient(135deg,${EP.lavender} 0%,${EP.lavLight} 100%)`, padding: "72px 28px 56px", textAlign: "center" }}>
            <SectionLabel center style={{ color: "rgba(255,255,255,.6)", marginBottom: 12 }}>The Blog</SectionLabel>
            <SectionHeading center light>Melanie's Latest Picks & Posts</SectionHeading>
            <Body center light style={{ maxWidth: 440, margin: "12px auto 0" }}>Honest product reviews, category highlights, and affiliate finds — updated regularly.</Body>
          </div>
          <Section>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 24 }} className="blog-grid">
              {POSTS.map((post, i) => (
                <div key={post.slug} onClick={() => setPage(`post/${post.slug}`)}
                  style={{ background: EP.white, borderRadius: 14, border: `1.5px solid ${EP.lavBorder}`, overflow: "hidden", cursor: "pointer", animation: `fadeUp .5s ${i * .1}s both`, transition: "transform .2s, box-shadow .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = `0 8px 24px rgba(139,107,174,.12)`; }}
                  onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}
                >
                  <div style={{ background: EP.lavSoft, height: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <div style={{ width: 44, height: 44, color: EP.lavender }} dangerouslySetInnerHTML={{ __html: ICONS[CATEGORIES.find(c => c.slug === post.categorySlug)?.icon || "misc"] }} />
                  </div>
                  <div style={{ padding: "20px 20px 24px" }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                      {postTag(post.category)}
                      <span style={{ fontFamily: "'Nunito',sans-serif", fontSize: 11, color: EP.light, alignSelf: "center" }}>{post.date}</span>
                    </div>
                    <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: EP.dark, lineHeight: 1.3, marginBottom: 10 }}>{post.title}</h3>
                    <p style={{ fontFamily: "'Nunito',sans-serif", fontSize: 14, color: EP.mid, lineHeight: 1.65, marginBottom: 16 }}>{post.excerpt.substring(0, 110)}...</p>
                    <div style={{ fontFamily: "'Nunito',sans-serif", fontWeight: 800, fontSize: 12, color: EP.lavender }}>Read More →</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>
          <Footer setPage={setPage} />
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          BLOG POST PAGE
      ════════════════════════════════════════════════════ */}
      {isPost && currentPost && (
        <div style={{ animation: "fadeIn .5s ease", paddingTop: 62 }}>
          <div style={{ background: EP.lavSoft, borderBottom: `1.5px solid ${EP.lavBorder}`, padding: "56px 28px 44px", textAlign: "center" }}>
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 16 }}>
              {postTag(currentPost.category)}
              <span style={{ fontFamily: "'Nunito',sans-serif", fontSize: 13, color: EP.light, alignSelf: "center" }}>{currentPost.date}</span>
            </div>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: "clamp(22px,3.5vw,36px)", color: EP.dark, lineHeight: 1.2, maxWidth: 680, margin: "0 auto 12px" }}>{currentPost.title}</h1>
            <div style={{ fontFamily: "'Nunito',sans-serif", fontSize: 14, color: EP.light }}>By <strong style={{ color: EP.mid }}>Melanie Byrd</strong></div>
          </div>
          <Section>
            <div style={{ maxWidth: 680, margin: "0 auto" }}>
              {currentPost.body.split("\n\n").map((para, i) => (
                <p key={i} style={{ fontFamily: "'Nunito',sans-serif", fontSize: 17, color: EP.dark, lineHeight: 1.85, marginBottom: 22 }}>{para}</p>
              ))}
              <div style={{ marginTop: 36, padding: 28, background: EP.lavSoft, borderRadius: 14, border: `1.5px solid ${EP.lavBorder}` }}>
                <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, color: EP.dark, marginBottom: 8 }}>Shop the Products</div>
                <Body style={{ marginBottom: 16 }}>Browse all of Melanie's hand-picked {currentPost.category} recommendations:</Body>
                <BtnPrimary onClick={() => setPage(`category/${currentPost.categorySlug}`)}>View {currentPost.category} Picks →</BtnPrimary>
              </div>
              <div style={{ marginTop: 24 }}>
                <button onClick={() => setPage("blog")} style={{ background: "none", border: "none", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: 14, color: EP.lavender, cursor: "pointer" }}>
                  ← Back to All Posts
                </button>
              </div>
            </div>
          </Section>
          <Footer setPage={setPage} />
        </div>
      )}

      {/* ════════════════════════════════════════════════════
          CONTACT PAGE
      ════════════════════════════════════════════════════ */}
      {isContact && (
        <div style={{ animation: "fadeIn .5s ease", paddingTop: 62 }}>
          <div style={{ background: `linear-gradient(135deg,${EP.lavender} 0%,${EP.lavLight} 100%)`, padding: "72px 28px 56px", textAlign: "center" }}>
            <SectionLabel center style={{ color: "rgba(255,255,255,.6)", marginBottom: 12 }}>Get in Touch</SectionLabel>
            <SectionHeading center light>Melanie Would Love to Hear From You!</SectionHeading>
            <Body center light style={{ maxWidth: 440, margin: "12px auto 0" }}>Questions, collaborations, brand partnerships, or just want to say hi — drop a note below!</Body>
          </div>
          <Section>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr", gap: 56, alignItems: "start" }} className="contact-grid">
              <div>
                <SectionLabel style={{ marginBottom: 10 }}>Contact Info</SectionLabel>
                <SectionHeading>Let's Connect!</SectionHeading>
                <Body style={{ marginBottom: 28 }}>Whether you're a brand interested in a partnership, a reader with a question, or just want to chat — I'm always happy to hear from you.</Body>
                {[
                  { icon: "📧", label: "Email",         val: "melanie@eproductpromotion.com" },
                  { icon: "📷", label: "Instagram",     val: "@eProductPromotion" },
                  { icon: "⏱️", label: "Response Time", val: "Within 48 hours" },
                ].map((c, i) => (
                  <div key={i} style={{ display: "flex", gap: 14, marginBottom: 20, alignItems: "flex-start" }}>
                    <div style={{ width: 40, height: 40, borderRadius: "50%", background: EP.lavPale, border: `1px solid ${EP.lavBorder}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{c.icon}</div>
                    <div>
                      <SectionLabel style={{ marginBottom: 3, fontSize: 9 }}>{c.label}</SectionLabel>
                      <Body style={{ fontSize: 14 }}>{c.val}</Body>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 24, padding: "16px 18px", background: EP.lavSoft, borderRadius: 12, border: `1.5px solid ${EP.lavBorder}` }}>
                  <SectionLabel style={{ marginBottom: 12 }}>Follow Us</SectionLabel>
                  <div style={{ display: "flex", gap: 9 }}>
                    {socials.map(s => <SocIcon key={s.name} {...s} />)}
                  </div>
                </div>
              </div>
              <div>
                {contactSent ? (
                  <div style={{ background: EP.white, borderRadius: 14, padding: "56px 40px", textAlign: "center", border: `1.5px solid ${EP.lavBorder}`, animation: "fadeUp .5s ease" }}>
                    <div style={{ fontSize: 48, marginBottom: 18 }}>💛</div>
                    <SectionHeading center>Message Sent!</SectionHeading>
                    <Body center style={{ marginBottom: 24 }}>Thank you so much! Melanie will get back to you within 48 hours.</Body>
                    <BtnOutline onClick={() => { setContactSent(false); setContactForm({ name: "", email: "", subject: "", message: "" }); }}>Send Another</BtnOutline>
                  </div>
                ) : (
                  <div style={{ background: EP.white, borderRadius: 14, padding: "36px 32px", border: `1.5px solid ${EP.lavBorder}` }}>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
                      <div>
                        <SectionLabel style={{ marginBottom: 7, fontSize: 9 }}>Your Name</SectionLabel>
                        <input className="ep-input" style={inputStyle} placeholder="Jane Smith" value={contactForm.name} onChange={e => setContactForm({ ...contactForm, name: e.target.value })} />
                      </div>
                      <div>
                        <SectionLabel style={{ marginBottom: 7, fontSize: 9 }}>Email Address</SectionLabel>
                        <input className="ep-input" style={inputStyle} placeholder="jane@email.com" value={contactForm.email} onChange={e => setContactForm({ ...contactForm, email: e.target.value })} />
                      </div>
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <SectionLabel style={{ marginBottom: 7, fontSize: 9 }}>Subject</SectionLabel>
                      <input className="ep-input" style={inputStyle} placeholder="What's this about?" value={contactForm.subject} onChange={e => setContactForm({ ...contactForm, subject: e.target.value })} />
                    </div>
                    <div style={{ marginBottom: 24 }}>
                      <SectionLabel style={{ marginBottom: 7, fontSize: 9 }}>Message</SectionLabel>
                      <textarea className="ep-input" style={{ ...inputStyle, resize: "vertical", minHeight: 130 }} placeholder="Tell Melanie what's on your mind..." value={contactForm.message} onChange={e => setContactForm({ ...contactForm, message: e.target.value })} />
                    </div>
                    <BtnPrimary onClick={submitContact} style={{ width: "100%", fontSize: 15, padding: "14px" }}>Send Message 💛</BtnPrimary>
                    <p style={{ fontFamily: "'Nunito',sans-serif", fontSize: 12, color: EP.light, textAlign: "center", marginTop: 14 }}>No spam — ever.</p>
                  </div>
                )}
              </div>
            </div>
          </Section>
          <Footer setPage={setPage} />
        </div>
      )}
    </div>
  );
}

// ── EXPORT ───────────────────────────────────────────────────
export default function EProductPromotion() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="*" element={<EProductInner />} />
      </Routes>
    </BrowserRouter>
  );
}
